import React, { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { Input } from '../Input/Input.js';
import { parseColorMathExpression, clampColorComponent, validateLCHAExpression } from '../../utils/linkedColorUtils.js';
import './LCHAInput.css';

export const LCHAInput = forwardRef(({
  component = 'l', // 'l', 'c', 'h', 'a'
  value = '',
  placeholder = '',
  referenceValue = 0,
  referenceOklch = null, // Full OKLCH object for relative chroma calculations
  /** Workspace gamut for `%` chroma and gamut-aware math */
  colorSpace = 'p3',
  onChange,
  onBlur,
  label,
  labelTooltip = '',
  className = '',
  disabled = false,
  ...props
}, ref) => {
  const [isOverridden, setIsOverridden] = useState(false);
  const [isValid, setIsValid] = useState(true);

  // Manual state management for LCHA editing to avoid sync issues
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const lastCommittedValueRef = useRef(value);
  const isExplicitlyCancelingRef = useRef(false);
  const editStartValueRef = useRef(value); // Store the value when editing starts
  
  // Update local value when prop changes (but not during editing)
  useEffect(() => {
    if (!isEditing && value !== lastCommittedValueRef.current) {
      setLocalValue(value);
      lastCommittedValueRef.current = value;
      editStartValueRef.current = value; // Keep edit start value in sync
    }
  }, [value, isEditing]);
  
  const startEditing = () => {
    // Store the current value when editing starts so we can revert to it
    editStartValueRef.current = value;
    setIsEditing(true);
  };
  
  const updateValue = (newValue) => {
    setLocalValue(newValue);
  };
  
  const commitValue = (val = localValue) => {
    setIsEditing(false);
    if (onChange && val !== lastCommittedValueRef.current) {
      onChange({ target: { value: val } });
      lastCommittedValueRef.current = val;
    }
    isExplicitlyCancelingRef.current = false;
  };
  

  // Component-specific configurations
  const componentConfig = {
    l: { min: 0, max: 1, step: 0.001, suffix: '' },
    c: { min: 0, max: 0.37, step: 0.001, suffix: '' },
    h: { min: 0, max: 360, step: 1, suffix: '' },
    a: { min: 0, max: 1, step: 0.001, suffix: '' }
  };

  const config = componentConfig[component] || componentConfig.l;

  // Update overridden state when value changes
  useEffect(() => {
    setIsOverridden(value && value.trim() !== '');
  }, [value]);

  // Format reference value for placeholder
  const formatReferenceValue = (refValue) => {
    if (component === 'h') {
      // Hue comes from Culori as 0-360 degrees already, don't multiply again
      return Math.round(refValue || 0).toString();
    }
    // Remove unnecessary decimal zeros: 0.150 -> 0.15, 1.000 -> 1
    const value = refValue || 0;
    return parseFloat(value.toFixed(3)).toString();
  };

  const placeholderText = placeholder || formatReferenceValue(referenceValue);

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Start editing on first change
    if (!isEditing) {
      startEditing();
    }
    
    // Update local value with debouncing
    updateValue(newValue);
    
    // Validate the expression (lightweight validation)
    const isValidExpression = validateLCHAExpression(newValue, component);
    setIsValid(isValidExpression !== null || newValue === '');
    setIsOverridden(newValue && newValue.trim() !== '');
    
    // Call the original onChange prop if provided
    if (onChange) {
      onChange(e);
    }
  }, [isEditing, startEditing, updateValue, component, onChange]);

  const handleBlur = useCallback((e) => {
    
    const trimmedValue = localValue.trim();
    
    if (trimmedValue === '') {
      // Empty value - use reference
      setIsOverridden(false);
      setIsValid(true);
      commitValue('');
    } else {
      // Validate and potentially clamp the expression
      const validatedExpression = validateLCHAExpression(trimmedValue, component);
      
      if (validatedExpression !== null) {
        // Calculate the result to show user what the expression evaluates to
        try {
          const result = parseColorMathExpression(trimmedValue, referenceValue, component, referenceOklch, null, colorSpace);
          clampColorComponent(result, component);
          
          // If the result is different from input, optionally show the calculated value
          // For now, keep the original expression
          setIsValid(true);
          commitValue(trimmedValue);
        } catch (error) {
          setIsValid(false);
          // Invalid expression - don't commit, just stop editing
          setIsEditing(false);
        }
      } else {
        setIsValid(false);
        // Invalid expression - don't commit, just stop editing
        setIsEditing(false);
      }
    }

    if (onBlur) {
      onBlur(e);
    }
  }, [localValue, component, referenceValue, referenceOklch, colorSpace, commitValue, onBlur]);

  // No cleanup needed for manual state management

  const handleFocus = useCallback(() => {
    if (!isEditing) {
      startEditing();
    }
  }, [isEditing, startEditing]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      // Commit the current value
      const trimmedValue = localValue.trim();
      
      if (trimmedValue === '') {
        // Empty value - use reference
        setIsOverridden(false);
        setIsValid(true);
        commitValue('');
      } else {
        // Validate and potentially clamp the expression
        const validatedExpression = validateLCHAExpression(trimmedValue, component);
        
        if (validatedExpression !== null) {
          // Test the expression with reference OKLCH for relative chroma
          try {
            const result = parseColorMathExpression(trimmedValue, referenceValue, component, referenceOklch, null, colorSpace);
            clampColorComponent(result, component);
            setIsValid(true);
            setIsOverridden(true);
            
            // Commit with the validated (potentially clamped) value
            commitValue(validatedExpression);
          } catch (error) {
            setIsValid(false);
            // Don't commit invalid values
          }
        } else {
          setIsValid(false);
          // Don't commit invalid values
        }
      }
      e.target.blur(); // Remove focus from input
    } else if (e.key === 'Escape') {
      // Make Escape behave like Enter - commit the current value
      const trimmedValue = localValue.trim();
      
      if (trimmedValue === '') {
        // Empty value - use reference
        setIsOverridden(false);
        setIsValid(true);
        commitValue('');
      } else {
        // Validate and potentially clamp the expression
        const validatedExpression = validateLCHAExpression(trimmedValue, component);
        
        if (validatedExpression !== null) {
          // Test the expression with reference OKLCH for relative chroma
          try {
            const result = parseColorMathExpression(trimmedValue, referenceValue, component, referenceOklch, null, colorSpace);
            clampColorComponent(result, component);
            setIsValid(true);
            setIsOverridden(true);
            
            // Commit with the validated (potentially clamped) value
            commitValue(validatedExpression);
          } catch (error) {
            setIsValid(false);
            // Don't commit invalid values, just blur
          }
        } else {
          setIsValid(false);
          // Don't commit invalid values, just blur
        }
      }
      e.target.blur(); // Remove focus from input
    }
  }, [localValue, component, referenceValue, referenceOklch, colorSpace, commitValue, value]);

  const inputClassName = [
    'lcha-input',
    isOverridden ? 'lcha-input--overridden' : 'lcha-input--placeholder',
    !isValid ? 'lcha-input--invalid' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className="lcha-input-wrapper">
      <Input
        ref={ref}
        type="text"
        style="outline"
        value={localValue}
        placeholder={placeholderText}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        label={label}
        labelTooltip={labelTooltip}
        disabled={disabled}
        className={inputClassName}
        {...props}
      />
      {component === 'h' && localValue && config.suffix && (
        <span className="lcha-input__suffix">{config.suffix}</span>
      )}
      {!isValid && (
        <div className="lcha-input__error">
          Invalid expression. Use numbers or math like "+0.1", "*0.5", "*0.5+0.1"
          {component === 'l' && ' or percentage adjustments like "+10%" (lighter) or "-20%" (darker)'}
          {component === 'c' && ' or simple percentages like "70%" for relative chroma'}
        </div>
      )}
    </div>
  );
});

LCHAInput.displayName = 'LCHAInput';
